<template>
    <div>
        <div v-if="!disabled" class="form-group mb-2">
            <autocomplete
                class="form-control"
                @place_changed="setPlace">
            </autocomplete>
        </div>
        <gmap-map
            :center="center"
            :zoom="12"
            style="width:100%; height: 400px;"
            @click="selectMarker"
        >
            <template v-for="(m, index) in markers">
                <gmap-marker
                    ref="markers"
                    :key="'marker_' + index"
                    :position="m.position"
                    @click="selectMarker"
                ></gmap-marker>
                <GmapCircle
                    v-if="hasRadius"
                    ref="circles"
                    :key="'radius_' + index"
                    :center="m.position"
                    :radius="m.position.radius * 1000"
                    :visible="true"
                    @radius_changed="payload => radiusChanged(payload, index)"
                    @center_changed="payload => centerChanged(payload, index)"
                    :options="{
                        fillColor:'red',
                        fillOpacity:.3,
                        strokeColor: '#BB0000',
                        strokeOpacity: 0.5,
                        strokeWeight: 1,
                        editable: !disabled,
                    }"
                ></GmapCircle>
            </template>
        </gmap-map>
    </div>
</template>

<script>
import { default as Autocomplete } from './Autocomplete';


export default {
    name: "GoogleMap",
    components: { Autocomplete },
    data() {
        return {
            // default to Montreal to keep it simple
            // change this to whatever makes sense
            center: { lat: 45.508, lng: -73.587 },
            markers: [],
            cachedRadius: 10,
            places: [],
            currentPlace: null,
            geocoder: null,
            addressStructure: {
                lat: 0,
                lng: 0,
                barangay: '',
                city: '',
                province: '',
                country: '',
                zipcode: ''
            },
            addressAllocation: {
                barangay: 'neighborhood',
                city: 'locality',
                province: 'administrative_area_level_2',
                country: 'country',
                zipcode: 'postal_code'
            }
        };
    },

    methods: {
        bindMarkersAndCircles() {
            if (!this.hasRadius) {
                return;
            }
            setTimeout(() => {
                for (const index in this.$refs.markers) {
                    if (!this.$refs.circles[index].$circleObject) {
                        continue;
                    }
                    this.$refs.circles[index].$circleObject.bindTo('center', this.$refs.markers[index].$markerObject, 'position');
                }
            }, 300)
        },

        radiusChanged(meters, index) {
            this.cachedRadius = this.markers[index].address.radius = this.markers[index].position.radius = meters / 1000; /*convert to km*/
            this.$emit('radiuschanged', this.markers);
        },

        centerChanged(location, index) {
            if (this.markers[index].position.lat == location.lat() && 
                this.markers[index].position.lng == location.lng()) {
                return;
            }

            this.saveMarker(location.lat(), location.lng());
        },

        getGeocoder() {
            return this.geocoder || (this.geocoder = new google.maps.Geocoder());
        },

        loadAddress(lat, lng) {
            lat = lat || 0;
            lng = lng || 0;
            return new Promise((resolve, reject) => {
                this.getGeocoder().geocode({ location: { lat, lng } }, (result, status) => {
                    if (status != "OK") {
                        reject(result, status);
                    } else {
                        let addresses = this.filterAddresses(result);
                        let address = this.transformData(addresses[0]);
                        resolve(address);
                    }
                });
            })
        },

        transformData(geocodedAddress) {
            let address = {
                ... this.addressStructure,
                lat: geocodedAddress.geometry.location.lat(),
                lng: geocodedAddress.geometry.location.lng(),
            };
            geocodedAddress.address_components.forEach(component => {
                for (const key in this.addressAllocation) {
                    if (component.types.includes(this.addressAllocation[key])) {
                        address[key] = component.long_name;
                        break;
                    }    
                }
            });

            return address;
        },

        filterAddresses(addresses) {
            let filteredAddresses = addresses.filter(address => !address.formatted_address
                .toLowerCase().includes("unnamed"));

            return filteredAddresses.length > 0
                ? filteredAddresses
                : addresses;
        },

        selectMarker(payload) {
            this.saveMarker(
                payload.latLng.lat(),
                payload.latLng.lng()
            );
        },

        saveMarker(lat, lng) {
            if (this.disabled) {
                return;
            }
            const radius = this.markers.length > 1 ? this.markers[0].radius : this.cachedRadius
            this.loadAddress(lat, lng)
            .then(response => {
                const data = {
                    address: { ...response, radius },
                    position: { lat, lng, radius }
                };
                this.multiple
                    ? this.markers.push(data)
                    : this.markers = [data];
                    
                this.bindMarkersAndCircles();
                this.$emit('input', this.markers);
                this.$emit('change', this.markers);
            })
            .catch((error, status) => {
                console.log(error, status)
            });
        },

        // receives a place object via the autocomplete component
        setPlace(place) {
            this.currentPlace = place;
            this.addMarker();
        },

        addMarker() {
            if (!this.currentPlace) {
                return;
            }
            const marker = {
                lat: this.currentPlace.geometry.location.lat(),
                lng: this.currentPlace.geometry.location.lng()
            };
            this.saveMarker(marker.lat, marker.lng);
            this.places.push(this.currentPlace);
            this.center = marker;
            this.currentPlace = null;
        },

        geolocate() {
            navigator.geolocation.getCurrentPosition(position => {
                this.center = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                };
            });
        },

        moveMapCenter({ lat, lng }) {
            lat = lat || 0;
            lng = lng || 0;
            this.center = { lat, lng };
        },

        recenter() {
            this.moveMapCenter(this.initialLocation);
        },

        resetMarkers() {
            if (this.initialValue) {
                this.markers = this.multiple ? this.initialValue : [this.initialValue];
                this.bindMarkersAndCircles();
            }
        },
    },

    watch: {
        initialLocation(newValue, oldValue) {
            this.moveMapCenter(newValue);
        }
    },

    props: {
        multiple: {
            default: false,
            type: Boolean,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        hasRadius: {
            type: Boolean,
            default: false,
        },
        initialLocation: {
            type: Object,
            default: () => ({
                lat: window.google_maps_default_lat || 14.5995124,
                lng: window.google_maps_default_lng || 120.9842195
            }),
        },
        initialValue: Object | Array
    },

    mounted() {
        this.moveMapCenter(this.initialLocation);
        // this.geolocate();
        this.resetMarkers();
    },

};
</script>